import { useMemo } from 'react';
import { get, merge, set, isEqual, cloneDeep, isNull, findIndex, has } from 'lodash';

import { PhoneService } from '../../../../entities/Phone/service';

export const INITIAL_VALUE = {
  isForwarderPhoneNumberValid: false,
  isDriverPhoneNumberValid: false,
  isTransportDocumentValid: false,
  tripIntention: 0,
  driver: {
    phone: {
      id: '',
      number: '',
    },
    id: '',
    isFaceId: false,
    isBlackList: false,
    lastName: '',
    firstName: '',
    middleName: '',
    createdDate: '',
    documents: [
      {
        type: 1,
        number: '',
        pictures: [],
      },
      {
        type: 3,
        number: '',
        pictures: [],
      },
    ],
  },
  forwarder: {
    phone: {
      id: '',
      number: '',
    },
    id: '',
    lastName: '',
    firstName: '',
    middleName: '',
    isFaceId: false,
    createdDate: '',
    documents: [
      {
        type: 3,
        number: '',
        pictures: [],
      },
    ],
  },
  vehicle: {
    licensePlate: '',
    createdDate: '',
    id: '',
    document: {
      type: 2,
      number: '',
      pictures: [],
    },
  },
  trailer: {
    licensePlate: '',
    createdDate: '',
    id: '',
    document: {
      type: 2,
      number: '',
      pictures: [],
    },
  },
  entryVehicleWeight: '',
  entrySecurityMessage: '',
  toleranceMessage: '',
  departureSecurityMessage: '',
  orderAdjustment: '',
  isNotLoaded: false,
  notLoadedMessage: '',
  orders: [],
  addedPerforationWeight: '0',
  packingLists: [],
  branchId: 0,
  statusId: 0,
  delayReasonsOrder: '',
  delayReasonsInvoice: '',
} as {
  [key: string]: any;
};

export type FormTripCont = typeof INITIAL_VALUE;

export const fillNullable = (data: any, fields: string[]) => {
  fields.forEach((field) => {
    if (!data[field]) {
      // eslint-disable-next-line
      data[field] = INITIAL_VALUE[field];
    }

    if (isEqual(field, 'orders')) {
      data[field].forEach((element: any, index: number) => {
        if (!element.netWeight) set(data, `orders[${index}].netWeight`, '0');
        if (!element.packageWeight) set(data, `orders[${index}].packageWeight`, '0');
      });
    }

    if ((isEqual(field, 'trailer') || isEqual(field, 'vehicle')) && (!data[field] || isNull(data[field].document))) {
      // eslint-disable-next-line
      data[field].document = INITIAL_VALUE[field].document;
    }
  });

  return data;
};

export const usePrepareValues = (id: number, values: any) =>
  useMemo(() => {
    const data = merge({}, INITIAL_VALUE, values);
    const driver = get(values, 'driver');
    const vehicle = get(values, 'vehicle');
    const forwarder = get(values, 'forwarder');
    const driverPhone = get(values, 'driver.phone.number');
    const forwarderPhone = get(values, 'forwarder.phone.number');

    if (Array.isArray(data?.forwarder?.documents)) {
      data.forwarder.documents = data.forwarder.documents.filter((document: any) => !!document?.number);
    }

    set(data, 'id', id);

    if (isEqual(driver?.documents?.length, 1)) {
      const driverDocs = cloneDeep(driver.documents);
      driverDocs.push({
        type: isEqual(driver.documents[0].type, 1) ? 3 : 1,
        number: '',
        pictures: [],
        pictureFiles: [],
      });
      set(data, 'driver.documents', driverDocs);
    }

    if (driverPhone) {
      set(data, 'driver.phone.number', PhoneService.formatPhone(driverPhone));
    }
    if (forwarderPhone) {
      set(data, 'forwarder.phone.number', PhoneService.formatPhone(forwarderPhone));
    }

    const hasDriverValidFromApi = has(values, 'isDriverDocumentValid');

    if (
      !driver ||
      !driver?.firstName ||
      !driver?.lastName ||
      !driver?.middleName ||
      !driver?.documents ||
      !driver?.documents?.length ||
      !driver?.documents[findIndex(driver?.documents, (doc: any) => doc.type === 1)]?.number ||
      (!driver?.documents[findIndex(driver?.documents, (doc: any) => doc.type === 1)]?.pictures &&
        !driver?.documents[findIndex(driver?.documents, (doc: any) => doc.type === 1)]?.pictureFiles)
    ) {
      if (!hasDriverValidFromApi) {
        set(data, 'isDriverDocumentValid', false);
      }
    }

    if (
      !forwarder ||
      !forwarder?.firstName ||
      !forwarder?.lastName ||
      !forwarder?.middleName ||
      !forwarder?.documents ||
      !forwarder?.documents?.length ||
      !forwarder?.documents[findIndex(forwarder?.documents, (doc: any) => doc.type === 3)]?.number ||
      (!forwarder?.documents[findIndex(forwarder?.documents, (doc: any) => doc.type === 3)]?.pictures &&
        !forwarder?.documents[findIndex(forwarder?.documents, (doc: any) => doc.type === 3)]?.pictureFiles)
    ) {
      set(data, 'isForwarderDocumentValid', false);
    }
    const hasTransportValidFromApi = has(values, 'isTransportDocumentValid');

    // eslint-disable-next-line
    if (!!vehicle) {
      if (
        !vehicle?.licensePlate ||
        !vehicle?.licensePlate?.length ||
        !vehicle?.document ||
        !vehicle?.document?.number ||
        // !values.entryVehicleWeight ||
        (!vehicle.document?.pictures?.length && !vehicle?.document?.pictureFiles?.length)
      ) {
        if (!hasTransportValidFromApi) {
          set(data, 'isTransportDocumentValid', false);
        }
      }
    }

    fillNullable(data, ['driver', 'forwarder', 'vehicle', 'trailer', 'orders', 'packingLists']);

    return data;
  }, [id, values]);
